import React, { useState, useEffect } from 'react';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ServiceListPage from './ServiceListPage';
import ProviderListPage from './ProviderListPage';
import CompanySearchPage from './CompanySearchPage';
import { getActiveServices } from '../services/api';
import { ListFilter, User, Building2 } from 'lucide-react';
import Hero from '../components/Hero';

function ServicesHomePage() {
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();
    const location = useLocation();

    const getActiveTabFromPath = (pathname) => {
        if (pathname === '/services') return 'services';
        if (pathname === '/company-register') return 'company_register';
        return 'providers';
    };

    const [activeTab, setActiveTab] = useState(getActiveTabFromPath(location.pathname));
    const [totalServiceCount, setTotalServiceCount] = useState(0);
    const [countLoading, setCountLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 768); // ✅ track small screens


    const handleSearchChange = (event) => setSearchQuery(event.target.value);
    const clearSearchQuery = () => setSearchQuery('');
    const [searchParams] = useSearchParams();
    const mdaId = searchParams.get('mdaId');

    // ✅ Update screen size on resize
    useEffect(() => {
        const handleResize = () => setIsSmallScreen(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);



    useEffect(() => {
        if (location.pathname === '/services') setActiveTab('services');
        else if (location.pathname === '/company-register') setActiveTab('company_register');
        else setActiveTab('providers');
    }, [location.pathname]);

    useEffect(() => {
        setCountLoading(true);
        getActiveServices({ type: 'public' })
            .then(response => setTotalServiceCount(response.data?.length || 0))
            .catch(() => setTotalServiceCount(0))
            .finally(() => setCountLoading(false));
    }, []);

    let searchPlaceholder = "Search...";
    let displayCount = null;

    if (activeTab === 'services') {
        searchPlaceholder = "Search All Services...";
        displayCount = (
            <span className="total-services-count">
                {countLoading ? '(loading...)' : `(${totalServiceCount} Services)`}
            </span>
        );
    } else if (activeTab === 'providers') {
        searchPlaceholder = "Search Service Providers...";
    } else if (activeTab === 'company_register') {
        searchPlaceholder = "Search Company Register...";
    }



    return (
        <div>
            {!isSmallScreen && (
                <Hero
                    searchQuery={searchQuery}
                    handleSearchChange={handleSearchChange}
                    isAuthenticated={isAuthenticated}
                    navigate={navigate}
                />
            )}

            <div className="services-home-container">
                <div className="services-tabs">
                    <button
                        className={`tab-button ${activeTab === 'providers' ? 'active' : ''}`}
                        onClick={() => navigate('/')}
                    >
                        <User size={16} /> Service Providers
                    </button>
                    <button
                        className={`tab-button ${activeTab === 'services' ? 'active' : ''}`}
                        onClick={() => navigate('/services')}
                    >
                        <ListFilter size={16} /> All Services
                    </button>
                    <button
                        className={`tab-button ${activeTab === 'company_register' ? 'active' : ''}`}
                        onClick={() => navigate('/company-register')}
                    >
                        <Building2 size={16} /> Company Register
                    </button>
                </div>

                <div className="services-tab-content">
                    {activeTab === 'providers' && (
                        <ProviderListPage searchQuery={searchQuery} clearSearchQuery={clearSearchQuery} />
                    )}
                    {activeTab === 'services' && (
                        <ServiceListPage key={mdaId} searchQuery={searchQuery} clearSearchQuery={clearSearchQuery} />
                    )}
                    {activeTab === 'company_register' && <CompanySearchPage />}
                </div>
            </div>
        </div>
    );
}

export default ServicesHomePage;
